import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showNewJobNotification,
  showMultipleJobsNotification,
} from "../../utils/notificationService";
import { fetchAllJobs } from "../../apis/JobApis";

const NotificationContext = createContext(null);

// Polling interval for checking new jobs (in milliseconds)
const POLL_INTERVAL = 60000; // 1 minute
const STORAGE_KEY = "ca_last_job_check";
const NOTIFICATION_PREF_KEY = "ca_notifications_enabled";

export function NotificationProvider({ children }) {
  const [permission, setPermission] = useState("default");
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [lastJobId, setLastJobId] = useState(null);
  const [newJobsCount, setNewJobsCount] = useState(0);
  const pollIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  // Initialize on mount
  useEffect(() => {
    const supported = isNotificationSupported();
    setIsSupported(supported);

    if (supported) {
      setPermission(getNotificationPermission());
      
      // Load user preference
      const savedPref = localStorage.getItem(NOTIFICATION_PREF_KEY);
      setIsEnabled(savedPref === "true");

      // Load last checked job ID
      const savedLastJobId = localStorage.getItem(STORAGE_KEY);
      if (savedLastJobId) {
        setLastJobId(savedLastJobId);
      }
    }
  }, []);

  // Request permission from user
  const requestPermission = useCallback(async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    
    if (result === "granted") {
      setIsEnabled(true);
      localStorage.setItem(NOTIFICATION_PREF_KEY, "true");
    }
    
    return result;
  }, []);

  // Toggle notifications on/off
  const toggleNotifications = useCallback(async () => {
    if (!isSupported) return false;

    if (permission !== "granted") {
      const result = await requestPermission();
      return result === "granted";
    }

    const newValue = !isEnabled;
    setIsEnabled(newValue);
    localStorage.setItem(NOTIFICATION_PREF_KEY, newValue.toString());
    return newValue;
  }, [isSupported, permission, isEnabled, requestPermission]);

  // Check for new jobs
  const checkForNewJobs = useCallback(async () => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    try {
      const response = await fetchAllJobs({ page: 1, size: 5, sort: "createdAt", order: "desc" });
      const jobs = response?.jobs || [];

      if (jobs.length === 0) {
        isPollingRef.current = false;
        return;
      }

      const latestJobId = jobs[0]?.id;

      // If we have a previous job ID to compare against
      if (lastJobId && latestJobId && latestJobId !== lastJobId) {
        // Find how many new jobs there are
        const lastJobIndex = jobs.findIndex(j => j.id === lastJobId);
        const newJobs = lastJobIndex === -1 ? jobs : jobs.slice(0, lastJobIndex);

        if (newJobs.length > 0 && isEnabled && permission === "granted") {
          // Update new jobs count for UI badge
          setNewJobsCount(prev => prev + newJobs.length);

          // Show notification only if tab is visible or in background
          if (document.visibilityState === "visible") {
            // Tab is active - show in-app notification (handled by Header component)
          } else {
            // Tab is in background - show browser notification
            if (newJobs.length === 1) {
              showNewJobNotification(newJobs[0]);
            } else {
              showMultipleJobsNotification(newJobs.length, newJobs);
            }
          }
        }
      }

      // Update last job ID
      if (latestJobId) {
        setLastJobId(latestJobId);
        localStorage.setItem(STORAGE_KEY, latestJobId);
      }
    } catch (error) {
      console.error("Error checking for new jobs:", error);
    } finally {
      isPollingRef.current = false;
    }
  }, [lastJobId, isEnabled, permission]);

  // Start/stop polling based on enabled state
  useEffect(() => {
    if (isEnabled && permission === "granted") {
      // Initial check
      checkForNewJobs();

      // Set up polling
      pollIntervalRef.current = setInterval(checkForNewJobs, POLL_INTERVAL);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    } else {
      // Clear polling if disabled
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [isEnabled, permission, checkForNewJobs]);

  // Clear new jobs count (called when user views jobs page)
  const clearNewJobsCount = useCallback(() => {
    setNewJobsCount(0);
  }, []);

  const value = {
    isSupported,
    permission,
    isEnabled,
    newJobsCount,
    requestPermission,
    toggleNotifications,
    checkForNewJobs,
    clearNewJobsCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
}

export default NotificationContext;
